import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    createProfessionalSituation,
    updateProfessionalSituation,
    getProfessionalSituation
} from '../helper/Api/AdminApi';

export default function ProfessionalSituation() {
    const [situations, setSituations] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentSituations, setCurrentSituations] = useState(null);

    const [formData, setFormData] = useState({
        Value: "",
        IsActive: "1"
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeOptions = [5, 10, 25, 50, 'All'];
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchProfessionalSituation();
    }, []);

    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showModal]);

    const fetchProfessionalSituation = async () => {
        setLoading(true);
        try {
            const res = await getProfessionalSituation();
            setSituations(res.Data || []);
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch Professional Situation records");
        } finally {
            setLoading(false);
        }
    };

    const filteredSituations = situations.filter(situ => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        return (
            (situ.Value?.toLowerCase() || '').includes(searchLower) ||
            (situ.IsActive?.toString() || '').toLowerCase().includes(searchLower)
        );
    });

    const totalItems = filteredSituations.length;
    const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));

    const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
    const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;

    const paginatedSituations = pageSize === 'All'
        ? filteredSituations
        : filteredSituations.slice(startIndex, endIndex);

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            const halfVisible = Math.floor(maxVisiblePages / 2);
            let start = currentPage - halfVisible;
            let end = currentPage + halfVisible;

            if (start < 1) {
                start = 1;
                end = maxVisiblePages;
            }

            if (end > totalPages) {
                end = totalPages;
                start = totalPages - maxVisiblePages + 1;
            }

            for (let i = start; i <= end; i++) pages.push(i);
        }

        return pages;
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePageChange = (page) => setCurrentPage(page);

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (pageSize === 'All') {
            setCurrentPage(1);
        }
    }, [pageSize]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.Value.trim()) newErrors.Value = "Value is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);

        try {
            const payload = {
                ID: currentSituations?.ID,
                Value: formData.Value,
                IsActive: formData.IsActive,
            };

            if (currentSituations) {
                const res = await updateProfessionalSituation(payload);

                setSituations((prev) =>
                    prev.map((item) =>
                        item.ID === currentSituations.ID
                            ? { ...item, Value: formData.Value, IsActive: formData.IsActive }
                            : item
                    )
                );

                toast.success(res.message || "Updated successfully!");
            } else {

                const res = await createProfessionalSituation(payload);

                setSituations((prev) => [
                    ...prev,
                    {
                        ID: res.Data?.ID,
                        Value: formData.Value,
                        IsActive: formData.IsActive
                    }
                ]);

                toast.success(res.message || "Created successfully!");
            }

            closeModal();
        } catch (err) {
            toast.error(err.response?.data?.message || "Operation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setCurrentSituations(item);
        setFormData({
            Value: item.Value,
            IsActive: item.IsActive === "1" || item.IsActive === true ? "1" : "0"
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setCurrentSituations(null);
        setFormData({ Value: "", IsActive: "1" });
        setErrors({});
    };

    return (
        <div className="card h-100 p-0 radius-12">

            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-sm text-secondary-light">Show:</span>
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                disabled={loading}
                            >
                                {pageSize === 'All' ? 'All' : pageSize}
                            </button>
                            <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                                {pageSizeOptions.map((option) => (
                                    <li key={option}>
                                        <button
                                            className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                                            onClick={() => handlePageSizeChange(option)}
                                            disabled={loading}
                                        >
                                            {option}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <form className="navbar-search">
                        <input
                            type="text"
                            className="form-control radius-8 h-40-px w-auto"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            disabled={loading}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                </div>

                <button
                    className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    disabled={loading}
                    onClick={() => {
                        closeModal();
                        setShowModal(true);
                    }}
                >
                    <Icon icon="ic:baseline-plus" className="icon text-lg line-height-1" />
                    Add New
                </button>
            </div>

            <div className="card-body p-24">
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading professional situations...</p>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center">Actions</th>
                                        <th>Value</th>
                                        <th>Is Active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedSituations.length > 0 ? (
                                        paginatedSituations.map((situ) => (
                                            <tr key={situ.ID}>
                                                <td className="text-center">
                                                    <div className='d-flex justify-content-center align-items-center'>
                                                        <button
                                                            type="button"
                                                            className="bg-primary-50 text-primary-600 w-32-px h-32-px rounded-circle d-flex justify-content-center align-items-center"
                                                            onClick={() => handleEdit(situ)}
                                                        >
                                                            <Icon icon="lucide:edit" className="icon" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>{situ.Value}</td>
                                                <td>{situ.IsActive === "1" || situ.IsActive === true ? "Yes" : "No"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="text-center py-4">
                                                {situations.length === 0 ? (
                                                    <div>
                                                        <p>No professional situations available</p>
                                                        <button
                                                            className="btn btn-sm btn-primary mt-2"
                                                            onClick={() => {
                                                                closeModal();
                                                                setShowModal(true);
                                                            }}
                                                        >
                                                            Add your first professional situation
                                                        </button>
                                                    </div>
                                                ) : (
                                                    "No records match your search"
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                            <div className="d-flex align-items-center gap-3">
                                <span className="text-secondary-light text-sm">
                                    Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                                    {Math.min(endIndex, totalItems)} of {totalItems} entries
                                </span>
                            </div>

                            {pageSize !== 'All' && totalPages > 1 && (
                                <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                            style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                                        >
                                            <Icon icon="ep:d-arrow-left" className="text-lg" />
                                        </button>
                                    </li>

                                    {getPageNumbers().map((page) => (
                                        <li className="page-item" key={page}>
                                            <button
                                                className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${currentPage === page
                                                        ? "bg-primary-600 text-white shadow-sm"
                                                        : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                                                    }`}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    ))}

                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                            style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                                        >
                                            <Icon icon="ep:d-arrow-right" className="text-lg" />
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* MODAL */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content radius-12">

                            <div className="modal-header">
                                <h4 className="modal-title">
                                    {currentSituations ? "Edit Professional Situation" : "Add New Professional Situation"}
                                </h4>
                                <button className="btn-close" disabled={loading} onClick={closeModal}></button>
                            </div>

                            <div className="modal-body">

                                <div className="mb-3">
                                    <label className="form-label">Value *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="Value"
                                        value={formData.Value}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                    />
                                    {errors.Value && <small className="text-danger">{errors.Value}</small>}
                                </div>

                                <div className="mb-4">
                                    <label className="form-label">Is Active *</label>
                                    <div className="d-flex gap-4 mt-2">

                                        <label className="form-check d-flex align-items-center">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                name="IsActive"
                                                value="1"
                                                checked={formData.IsActive === "1"}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                            />
                                            <span className="ms-2">Yes</span>
                                        </label>

                                        <label className="form-check d-flex align-items-center">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                name="IsActive"
                                                value="0"
                                                checked={formData.IsActive === "0"}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                            />
                                            <span className="ms-2">No</span>
                                        </label>

                                    </div>
                                </div>


                                <div className="d-flex justify-content-center gap-3 mt-4">
                                    <button className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8" disabled={loading} onClick={closeModal}>
                                        Close
                                    </button>

                                    <button className="btn btn-primary px-56 py-10" disabled={loading} onClick={handleSubmit}>
                                        {loading ? "Processing..." : "Save"}
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}